import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createServicePageTemplate } from '../../content/servicePageTemplate';
import { slugify } from '../slugify';
import AdminModal from '../components/AdminModal';
import ConfirmDialog from '../components/ConfirmDialog';
import AdminPagination from '../components/AdminPagination';
import { usePagination } from '../hooks/usePagination';
import { useAdminPersist } from '../AdminPersistContext';
import { useEditorFeedback } from '../hooks/useEditorFeedback';

const PAGE_SIZE = 8;

/**
 * "Service Pages" admin tab: list + full CRUD for the rich, inline-WYSIWYG
 * service page template (TemplateServicePage). Editing itself happens on a
 * dedicated full-viewport route (see ServicePageEditor.jsx) since inline
 * WYSIWYG needs the whole page, not a modal — this tab is just the index.
 */
export default function ServicePagesEditor({ content, onChange }) {
  const navigate = useNavigate();
  const [deleteSlug, setDeleteSlug] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('New Service Page');
  const [creating, setCreating] = useState(false);
  const { persistNow } = useAdminPersist();
  const feedback = useEditorFeedback({ persistNow });

  const list = content.servicePages?.list ?? [];
  const pages = content.servicePages?.pages ?? {};

  const { page, setPage, totalPages, paginatedItems, totalItems } = usePagination(list, PAGE_SIZE);

  const openCreateModal = () => {
    setCreateTitle('New Service Page');
    setCreateOpen(true);
  };

  const closeCreateModal = () => {
    if (creating) return;
    setCreateOpen(false);
  };

  const handleCreate = async (e) => {
    e?.preventDefault();
    const title = createTitle.trim() || 'New Service Page';

    const base = slugify(title) || 'service-page';
    const isTaken = (candidate) => list.some((s) => s.slug === candidate) || Boolean(pages[candidate]);
    let slug = base;
    let i = 2;
    while (isTaken(slug)) {
      slug = `${base}-${i}`;
      i += 1;
    }

    const now = new Date().toISOString();
    const nextList = [...list, { slug, title, createdAt: now, updatedAt: now }];
    const nextPages = {
      ...pages,
      [slug]: {
        slug,
        title,
        data: createServicePageTemplate(slug, title),
        styles: {},
        createdAt: now,
        updatedAt: now,
      },
    };
    onChange({ ...content, servicePages: { list: nextList, pages: nextPages } });

    setCreating(true);
    try {
      await persistNow();
    } catch {
      /* the full-page editor will retry saving; missing the initial save isn't fatal */
    } finally {
      setCreating(false);
    }
    setCreateOpen(false);
    navigate(`/admin/service-pages/${slug}/edit`);
  };

  const confirmRemove = () => {
    const slug = deleteSlug;
    if (!slug) return;
    const nextList = list.filter((s) => s.slug !== slug);
    const nextPages = { ...pages };
    delete nextPages[slug];
    onChange({ ...content, servicePages: { list: nextList, pages: nextPages } });
    setDeleteSlug(null);
    feedback.notifySaved('Service page removed. Saving to the server…');
  };

  return (
    <div className="admin-panel">
      <div className="admin-panel__head">
        <div>
          <h2>Service Pages</h2>
          <p className="admin-panel__desc">
            Rich, fully inline-editable service pages (hero, feature grids, FAQ, pricing tiers, lead-capture
            form…). Create one from the template, then use <strong>Edit</strong> for on-page WYSIWYG editing.
          </p>
        </div>
        <button type="button" className="admin-btn admin-btn--primary" onClick={openCreateModal}>
          + Create Service Page
        </button>
      </div>

      <div className="admin-toolbar">
        <span className="admin-toolbar__meta">{totalItems} page(s)</span>
      </div>

      <div className="admin-list">
        {paginatedItems.map((s) => (
          <div key={s.slug} className="admin-list-item">
            <div>
              <strong>{s.title}</strong>
              <br />
              <a href={`/services/${s.slug}`} target="_blank" rel="noopener noreferrer">
                /services/{s.slug}
              </a>
            </div>
            <div className="admin-list-actions">
              <button
                type="button"
                className="admin-btn admin-btn--ghost admin-btn--sm"
                onClick={() => navigate(`/admin/service-pages/${s.slug}/edit`)}
              >
                Edit
              </button>
              <button
                type="button"
                className="admin-btn admin-btn--danger admin-btn--sm"
                onClick={() => setDeleteSlug(s.slug)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {!totalItems ? (
          <p className="admin-panel__desc">No service pages yet. Click "Create Service Page" to get started.</p>
        ) : null}
      </div>

      <AdminPagination
        page={page}
        totalPages={totalPages}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      <AdminModal open={createOpen} onClose={closeCreateModal} title="Create service page" size="sm">
        <form onSubmit={handleCreate}>
          <div className="admin-field">
            <label htmlFor="new-service-page-title">Title for the new service page</label>
            <input
              id="new-service-page-title"
              type="text"
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              autoFocus
              required
            />
            <span className="admin-field-hint">
              /services/{slugify(createTitle) || '…'}
            </span>
          </div>
          <div className="admin-form-actions">
            <button type="button" className="admin-btn admin-btn--ghost" onClick={closeCreateModal} disabled={creating}>
              Cancel
            </button>
            <button type="submit" className="admin-btn admin-btn--primary" disabled={creating}>
              {creating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </AdminModal>

      <ConfirmDialog
        open={!!deleteSlug}
        title="Delete service page"
        message={`Delete "${list.find((s) => s.slug === deleteSlug)?.title || deleteSlug}"? This removes the public /services/${deleteSlug} page once saved. This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={confirmRemove}
        onCancel={() => setDeleteSlug(null)}
      />

      {feedback.dialogs}
    </div>
  );
}
